import { useState, useEffect, useCallback } from 'react';
import { 
  MagnifyingGlassIcon,
  PlusIcon,
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  UserIcon,
  BuildingOfficeIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  XCircleIcon,
  KeyIcon
} from '@heroicons/react/24/outline';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import { usersAPI, companiesAPI } from '../../lib/api';
import { useCompanies } from '../../hooks/useSharedData';
import { useRoles } from '../../hooks/useRoles';
import UserModal from './UserModal';

// Helper function to format phone number for display (show only 10 digits)
const formatPhoneForDisplay = (phoneNumber) => {
  if (!phoneNumber) return 'N/A';
  
  // Remove all non-digit characters
  const digitsOnly = phoneNumber.replace(/\D/g, '');
  
  // If it's 12 digits and starts with 91, show only the last 10 digits
  if (digitsOnly.length === 12 && digitsOnly.startsWith('91')) {
    return digitsOnly.substring(2);
  }
  
  // If it's 10 digits, show as is
  if (digitsOnly.length === 10) {
    return digitsOnly;
  }
  
  // For other cases, return the original
  return phoneNumber;
};

// Reset Password Modal Component
const ResetPasswordModal = ({ user, onClose, onReset, loading }) => {
  const [formData, setFormData] = useState({
    new_password: ''
  });
  const [errors, setErrors] = useState({});

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    const newErrors = {};
    if (!formData.new_password.trim()) {
      newErrors.new_password = 'New password is required';
    } else if (formData.new_password.length < 6) {
      newErrors.new_password = 'New password must be at least 6 characters';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onReset(user.id, formData.new_password);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Reset Password
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XCircleIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            <span className="font-medium">User:</span> {user.first_name} {user.middle_name} {user.last_name}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            <span className="font-medium">Email:</span> {user.email}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              New Password
            </label>
            <input
              type="password"
              value={formData.new_password}
              onChange={(e) => handleInputChange('new_password', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                errors.new_password ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
              }`}
              placeholder="Enter new password"
              disabled={loading}
            />
            {errors.new_password && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.new_password}</p>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={loading}
              type="button"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Password'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    role: '',
    company_id: '',
    is_active: '',
    phone: '',
    dgp_id: ''
  });
  const [localPhone, setLocalPhone] = useState('');
  const [localDgpId, setLocalDgpId] = useState('');
  const [pagination, setPagination] = useState({ limit: 10 });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  // const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importPreview, setImportPreview] = useState([]);
  const [showImportResultModal, setShowImportResultModal] = useState(false);
  const [importResults, setImportResults] = useState({ successCount: 0, errorCount: 0, errorDetails: [] });
  
  // Get companies data using the hook
  const { data: companies } = useCompanies();
  
  // State for total counts from entire database
  const [totalCounts, setTotalCounts] = useState({
    totalUsers: 0,
    activeUsers: 0,
    adminUsers: 0,
    superAdminUsers: 0,
    totalCompanies: 0
  });
  
  // Fetch roles from API
  const { data: rolesResponse, isLoading: rolesLoading, error: rolesError } = useRoles();
  const roles = rolesResponse?.data?.data || [];
  
  // Fallback roles if API fails due to permissions
  const fallbackRoles = [
    { id: 1, name: 'admin' },
    { id: 2, name: 'executive' },
    { id: 3, name: 'company_lead' },
    { id: 4, name: 'officer' },
    { id: 5, name: 'viewer' }
  ];
  
  // Use fallback roles if there's an error (like 403 permission denied)
  const availableRoles = rolesError ? fallbackRoles : roles;

  
  useEffect(() => {
    fetchUsers();
    fetchTotalCounts();
  }, [pagination.page, pagination.limit, filters]);

  // Update companies count when companies data is loaded
  useEffect(() => {
    if (companies?.data?.data) {
      const companiesCount = companies.data.data.length;
      console.log('🔍 Companies data loaded, updating count:', companiesCount);
      setTotalCounts(prev => ({
        ...prev,
        totalCompanies: companiesCount
      }));
    }
  }, [companies]);

 
const fetchUsers = async () => {
  try {
    setLoading(true);
    console.log('🔍 Fetching users...');
    const params = {
      page: pagination.page || 1,
      limit: pagination.limit || 10,
      search: searchTerm || undefined,
      ...Object.fromEntries(
        Object.entries(filters).filter(([key, value]) => value !== '')
      )
    };
    console.log('📋 Fetch params:', params);
    const response = await usersAPI.getAll(params);
    console.log('✅ Users API Response:', response);
    setUsers(response.data.data);
    setPagination(response.data.pagination);
  } catch (error) {
    console.error('❌ Error fetching users:', error);
    setUsers([]);
    setPagination({});
    alert('❌ Failed to load users. Please try again.');
  } finally {
    setLoading(false);
    console.log('🏁 Fetch users completed');
  }
};

const handleResetPassword = async (userId, newPassword) => {
  try {
    setActionLoading(true);
    await usersAPI.resetPassword(userId, { new_password: newPassword });
    alert('✅ Password reset successfully!');
    setShowResetPasswordModal(false);
    setResetPasswordUser(null);
  } catch (error) {
    console.error('Error resetting password:', error);
    const errorMsg = error.response?.data?.message || error.message || 'Failed to reset password';
    alert(`❌ ${errorMsg}`);
  } finally {
    setActionLoading(false);
  }
};

const handleDeleteUser = async (userId) => {
  if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
    try {
      setActionLoading(true);
      await usersAPI.delete(userId);
      alert('User deleted successfully!');
      fetchUsers(); // Refresh the user list
      fetchTotalCounts(); // Refresh the counts
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user. Please try again.');
    } finally {
      setActionLoading(false);
    }
  }
};

const fetchTotalCounts = async () => {
  try {
    console.log('🔍 Fetching total counts...');
    
    // Get total users count (including deleted users) 
    console.log('🔍 Calling API with include_deleted=true...');
    const totalUsersResponse = await usersAPI.getAll({ limit: 1, include_deleted: true });
    console.log('🔍 Total users response:', totalUsersResponse);
    const totalUsersCount = totalUsersResponse.data.pagination?.total || 0;
    console.log('🔍 Calculated total users count:', totalUsersCount);
    
    // Use limit=1 to minimize data transfer but still get full pagination info
    const response = await usersAPI.getAll({ limit: 1 });
    const pagination = response.data.pagination;
    
    // Get active users count (users where active = true)
    const activeUsersResponse = await usersAPI.getAll({ 
      limit: 1, // Just need count, not data
      is_active: true 
    });
    const activeUsersCount = activeUsersResponse.data.pagination?.total || 0;
    
    // Get superadmin count by filtering for superadmin role
    const superadminResponse = await usersAPI.getAll({ 
      limit: 1, // Just need count, not data
      role: 'superadmin' 
    });
    const superadminCount = superadminResponse.data.pagination?.total || 0;
    
    setTotalCounts(prev => ({
      totalUsers: totalUsersCount, // All users including deleted ones
      activeUsers: activeUsersCount, // Active users = users where active = true
      adminUsers: 0, // Keep for backward compatibility
      superAdminUsers: superadminCount,
      totalCompanies: prev.totalCompanies // Keep existing companies count, will be updated by useEffect
    }));
    
  } catch (error) {
    console.error('Error fetching total counts:', error);
    // Keep existing counts on error
  }
};

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchUsers();
  };

  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  const handlePhoneChange = useCallback((e) => {
    setLocalPhone(e.target.value);
  }, []);

  const handleDgpIdChange = useCallback((e) => {
    setLocalDgpId(e.target.value);
  }, []);

  const handlePhoneKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      setFilters(prev => ({ ...prev, phone: localPhone }));
      setPagination(prev => ({ ...prev, page: 1 }));
    }
  }, [localPhone]);

  const handleDgpIdKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      setFilters(prev => ({ ...prev, dgp_id: localDgpId }));
      setPagination(prev => ({ ...prev, page: 1 }));
    }
  }, [localDgpId]);

  const handleClearFilters = () => {
    setFilters({
      role: '',
      company_id: '',
      is_active: '',
      phone: '',
      dgp_id: ''
    });
    setLocalPhone('');
    setLocalDgpId('');
    setSearchTerm('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageSizeChange = (newLimit) => {
    setPagination(prev => ({ ...prev, limit: parseInt(newLimit), page: 1 }));
  };

  // CSV Import Functions
  // const parseCSV = (csvText) => {
  //   const lines = csvText.split('\n');
  //   const headers = lines[0].split(',').map(h => h.trim());
  //   const data = [];
    
  //   for (let i = 1; i < lines.length; i++) {
  //     if (lines[i].trim()) {
  //       const values = lines[i].split(',').map(v => v.trim());
  //       const row = {};
  //       headers.forEach((header, index) => {
  //         row[header.toLowerCase().replace(/\s+/g, '_')] = values[index] || '';
  //       });
  //       data.push(row);
  //     }
  //   }
  //   return data;
  // };

  const parseCSV = (csvText) => {
    const lines = csvText.replace(/\r/g, '').split('\n').filter(line => line.trim()); // normalize
    const headers = lines[0].replace(/^\uFEFF/, '').split(',').map(h => h.trim());
    const data = [];
  
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      if (values.length === 0 || values.every(v => v === '')) continue;
  
      const row = {};
      headers.forEach((header, index) => {
        const key = header.toLowerCase().replace(/\s+/g, '_');
        let val = values[index];

        if (val === undefined || val === '') {
          row[key] = null;
        } else if (key === 'phone') {
          // Keep phone numbers as strings
          row[key] = val.toString();
        } else if (/^\d+$/.test(val)) {
          row[key] = Number(val);
        } else if (/^(true|false)$/i.test(val)) {
          row[key] = val.toLowerCase() === 'true';
        } else {
          row[key] = val;
        }
      });
      data.push(row);
    }
    return data;
  };

  const generatePassword = (email) => {
    // Generate a password based on email and a random number
    const emailPrefix = email.split('@')[0];
    const randomNum = Math.floor(Math.random() * 1000);
    return `${emailPrefix}${randomNum}!`;
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      setCsvFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        const csvText = e.target.result;
        const parsedData = parseCSV(csvText);
        setImportPreview(parsedData);
      };
      reader.readAsText(file);
    } else {
      alert('Please select a valid CSV file');
    }
  };

  const handleImportUsers = async () => {
    if (!csvFile || importPreview.length === 0) {
      alert('Please select a CSV file first');
      return;
    }

    setImportLoading(true);
    try {
      // Get the first available company ID (optional fallback removed; using company_id from CSV)

      // Map CSV data to user format
      const usersToImport = importPreview.map((row, index) => {
        // const email = row.email_id || row.email || '';
        // const badgeNumber = parseInt(row.sevarth_id || row.badge_no || '0');
        
        // Create user object
        const userData = {
          first_name: row.first_name,
          middle_name: row.middle_name,
          last_name: row.last_name,
          // full_name: `${row.first_name || ''} ${row.middle_name || ''} ${row.last_name}`.trim(),
          email: row.email || row.email_id,
          phone: row.phone || null,
          dgp_id: row.dgp_id,
          designation_id: row.designation_id,
          role: row.role || 'viewer',
          password: generatePassword(row.email || row.email_id || ''),
          is_active: row.active !== undefined ? row.active : true,
          company_id: row.company_id,
          badge_no: row.badge_no ,
          icard_no: row.icard_no || null,
        };

      //   // Only add badge_no if it's a valid positive number
      //   if (badgeNumber > 0) {
      //     userData.badge_no = badgeNumber;
      //   }
      //   // If badge_no is 0 or invalid, don't include the field to avoid validation error

     return userData;
   });

      // Import users one by one (you can modify this to use a bulk API if available)
      let successCount = 0;
      let errorCount = 0;
      let errorDetails = [];

      for (const user of usersToImport) {
        try {
          await usersAPI.create(user);
          successCount++;
        } catch (error) {
          console.error('Error importing user:', user.email, error);
          errorCount++;
          
          // Collect detailed error information for final summary
          const errorMessage = error.response?.data?.error?.message || error.response?.data?.message || error.message || 'Unknown error';
          const field = error.response?.data?.error?.field;
          const validationErrors = error.response?.data?.details || {};

          // Store detailed error information (no individual popups)
          errorDetails.push({
            user_id: user.email || `Row ${usersToImport.indexOf(user) + 1}`,
            company_id: user.company_id || 'N/A',
            name: `${user.first_name || ''} ${user.middle_name || ''} ${user.last_name || ''}`.trim() || 'Unknown',
            email: user.email || 'N/A',
            badge_no: user.badge_no || 'N/A',
            error_message: errorMessage,
            field: field || 'General',
            details: validationErrors
          });
        }
      }

      // Store results and show custom modal instead of alert
      setImportResults({
        successCount,
        errorCount,
        errorDetails
      });
      
      setShowImportResultModal(true);
      
      // Reset and refresh
      setShowImportModal(false);
      setCsvFile(null);
      setImportPreview([]);
      fetchUsers();
      fetchTotalCounts();
      
    } catch (error) {
      console.error('Error importing users:', error);
      alert('Error importing users. Please check the console for details.');
    } finally {
      setImportLoading(false);
    }
  };



     const getRoleColor = (role) => {
     switch (role) {
       case 'admin': return 'danger';
       case 'executive': return 'warning';
       case 'company_lead': return 'info';
       case 'officer': return 'primary';
       case 'viewer': return 'secondary';
       default: return 'secondary';
     }
   };

  const getStatusColor = (isActive) => {
    return isActive ? 'success' : 'danger';
  };

  const getStatusText = (isActive) => {
    return isActive ? 'Active' : 'Inactive';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-secondary dark:bg-gray-900">
        <div className="max-w-[1600px] mx-auto px-6 py-6">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded-lg w-1/4 mb-6"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-24 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
              ))}
            </div>
            <div className="h-96 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-secondary dark:bg-gray-900">
      <div className="max-w-[1600px] mx-auto px-6 py-6">
        
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">
              Users Management
            </h1>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => setShowImportModal(true)}
              className="flex items-center gap-1 px-3 py-1.5 text-xs"
            >
              <UserIcon className="h-3 w-3" />
              Import User
            </Button>
            <Button
              variant="primary"
              onClick={() => setShowUserModal(true)}
              className="flex items-center gap-1 px-3 py-1.5 text-xs"
            >
              <PlusIcon className="h-3 w-3" />
              Add User
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card className="p-4 h-[100px] border border-[#E6EAF2] dark:border-gray-700">
            <div className="flex items-center justify-between h-full">
              <div className="flex-1">
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
                  Total Users
                </p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {totalCounts.totalUsers}
                </p>
              </div>
              <div className="p-1.5 bg-brand-primary/10 rounded-full">
                <UserIcon className="h-4 w-4 text-brand-primary" />
              </div>
            </div>
          </Card>

          <Card className="p-4 h-[100px] border border-[#E6EAF2] dark:border-gray-700">
            <div className="flex items-center justify-between h-full">
              <div className="flex-1">
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
                  Active Users
                </p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {totalCounts.activeUsers}
                </p>
              </div>
              <div className="p-1.5 bg-semantic-pos/10 rounded-full">
                <CheckCircleIcon className="h-4 w-4 text-semantic-pos" />
              </div>
            </div>
          </Card>

          <Card className="p-4 h-[100px] border border-[#E6EAF2] dark:border-gray-700">
            <div className="flex items-center justify-between h-full">
              <div className="flex-1">
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
                  Super Admin
                </p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {totalCounts.superAdminUsers} 
                </p>
              </div>
              <div className="p-1.5 bg-semantic-warning/10 rounded-full">
                <ShieldCheckIcon className="h-4 w-4 text-semantic-warning" />
              </div>
            </div>
          </Card>

          <Card className="p-4 h-[100px] border border-[#E6EAF2] dark:border-gray-700">
            <div className="flex items-center justify-between h-full">
              <div className="flex-1">
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
                  Companies
                </p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {totalCounts.totalCompanies}
                </p>
              </div>
              <div className="p-1.5 bg-brand-accent/10 rounded-full">
                <BuildingOfficeIcon className="h-4 w-4 text-brand-accent" />
              </div>
            </div>
          </Card>
        </div>

        {/* Search and Filters */}
        <Card className="border border-[#E6EAF2] dark:border-gray-700 mb-6">
          <div className="p-4">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search users by name, email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="w-full pl-10 pr-4 py-2 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-accent"
                  />
                </div>
              </div>

              {/* Filter Toggle */}
              <Button
                variant="secondary"
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-1 px-3 py-2 text-xs"
              >
                <FunnelIcon className="h-3 w-3" />
                Filters
              </Button>

              {/* Search Button */}
              <Button
                variant="primary"
                onClick={handleSearch}
                className="px-4 py-2 text-xs"
              >
                Search
              </Button>
            </div>

            {/* Filters Panel */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Role
                    </label>
                    <select
                      value={filters.role}
                      onChange={(e) => handleFilterChange('role', e.target.value)}
                      className="w-full px-3 py-2 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-accent"
                      disabled={rolesLoading}
                    >
                      <option value="">All Roles</option>
                      {rolesLoading ? (
                        <option value="">Loading roles...</option>
                      ) : (
                        availableRoles.map(role => (
                          <option key={role.id} value={role.name}>
                            {role.name.charAt(0).toUpperCase() + role.name.slice(1).replace('_', ' ')}
                          </option>
                        ))
                      )}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Company
                    </label>
                    <select
                      value={filters.company_id}
                      onChange={(e) => handleFilterChange('company_id', e.target.value)}
                      className="w-full px-3 py-2 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-accent"
                    >
                      <option value="">All Companies</option>
                      {companies?.data?.data?.map(company => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Status
                    </label>
                    <select
                      value={filters.is_active}
                      onChange={(e) => handleFilterChange('is_active', e.target.value)}
                      className="w-full px-3 py-2 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-accent"
                    >
                      <option value="">All Status</option>
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>

                  {/* Phone Filter */}
                  <div className="space-y-1">
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                      Phone
                    </label>
                    <input
                      key="phone-filter"
                      type="text"
                      value={localPhone}
                      onChange={handlePhoneChange}
                      onKeyPress={handlePhoneKeyPress}
                      placeholder="Enter phone number and press Enter..."
                      className="w-full px-3 py-2 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-accent"
                    />
                  </div>

                  {/* DGP ID Filter */}
                  <div className="space-y-1">
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                      DGP ID
                    </label>
                    <input
                      key="dgp-id-filter"
                      type="text"
                      value={localDgpId}
                      onChange={handleDgpIdChange}
                      onKeyPress={handleDgpIdKeyPress}
                      placeholder="Enter DGP ID and press Enter..."
                      className="w-full px-3 py-2 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-accent"
                    />
                  </div>

                </div>
                
                {/* Clear Filters Button */}
                <div className="mt-4 flex justify-end">
                  <Button
                    variant="secondary"
                    onClick={handleClearFilters}
                    className="flex items-center gap-1 px-3 py-2 text-xs"
                  >
                    Clear Filters
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Users Table */}
        <Card>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border border-teal-700">
                <thead>
                  <tr className="bg-teal-700 border-b border-teal-600 dark:border-teal-600 divide-x divide-teal-600 dark:divide-teal-600">
                    <th className="text-left py-3 px-4 font-medium text-white dark:text-white">Name</th>
                    <th className="text-left py-3 px-4 font-medium text-white dark:text-white">Email</th>
                    <th className="text-left py-3 px-4 font-medium text-white dark:text-white">Phone</th>
                    <th className="text-left py-3 px-4 font-medium text-white dark:text-white">Badge No</th>
                    <th className="text-left py-3 px-4 font-medium text-white dark:text-white">DGP ID</th>
                    <th className="text-left py-3 px-4 font-medium text-white dark:text-white">Company</th>
                    <th className="text-left py-3 px-4 font-medium text-white dark:text-white">Designation</th>
                    <th className="text-left py-3 px-4 font-medium text-white dark:text-white">Role</th>
                    <th className="text-left py-3 px-4 font-medium text-white dark:text-white">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-white dark:text-white">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className="border-b border-teal-600 dark:border-teal-600 hover:bg-gray-50 dark:hover:bg-gray-800 divide-x divide-teal-600 dark:divide-teal-600">
                      <td className="py-3 px-4">
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">{user.full_name}</div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-900 dark:text-white">{user.email}</td>
                      <td className="py-3 px-4 text-gray-900 dark:text-white">{formatPhoneForDisplay(user.phone_number)}</td>
                      <td className="py-3 px-4 text-gray-900 dark:text-white">{user.badge_no || 'N/A'}</td>
                      <td className="py-3 px-4 text-gray-900 dark:text-white">{user.dgp_id || 'N/A'}</td>
                      <td className="py-3 px-4 text-gray-900 dark:text-white">{user.company_name || 'N/A'}</td>
                      <td className="py-3 px-4 text-gray-900 dark:text-white">{user.designation_name || user.designation || 'N/A'}</td> 
                      <td className="py-3 px-4">
                        <Badge variant={getRoleColor(user.role_name)}>
                          {user.role_name?.replace('_', ' ').toUpperCase() || 'N/A'}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant={getStatusColor(user.active)}>
                          {getStatusText(user.active)}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setSelectedUser({ ...user, viewMode: true });
                              setShowUserModal(true);
                            }}
                            className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                            title="View User"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedUser({ ...user, viewMode: false });
                              setShowUserModal(true);
                            }}
                            className="p-1 text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors"
                            title="Edit User"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              setResetPasswordUser(user);
                              setShowResetPasswordModal(true);
                            }}
                            className="p-1 text-purple-600 hover:text-purple-700 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded transition-colors"
                            title="Reset Password"
                          >
                            <KeyIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user.id)}
                            className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                            title="Delete User"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {users.length === 0 && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  No users found
                </div>
              )}
            </div>
          )}

          {/* Pagination */}
          {(pagination.totalPages > 1 || pagination.total > 0) && (
                  <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700 px-4 pb-4">

              {/* Page Size Selector */}
              <div className="flex items-center gap-2 mb-3 text-xs text-gray-600 dark:text-gray-400">
                <span>Show :</span>
                <select
                  value={pagination.limit || 10}
                  onChange={(e) => handlePageSizeChange(e.target.value)}
                  className="pl-2 pr-6 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs bg-white dark:bg-gray-800 dark:text-white"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span>users per page</span>
              </div>

              <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
                <span>
                  Showing {((pagination.page || 1) - 1) * (pagination.limit || 10) + 1} to {Math.min((pagination.page || 1) * (pagination.limit || 10), pagination.total || 0)} of {pagination.total || 0} users
                </span>
                {pagination.totalPages > 1 && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      onClick={() => setPagination(prev => ({ ...prev, page: (prev.page || 1) - 1 }))}
                      disabled={!pagination.hasPrev}
                      className="px-2 py-1 text-xs"
                    >
                      Previous
                    </Button>
                    <span className="px-2 py-1">
                      Page {pagination.page || 1} of {pagination.totalPages || 0}
                    </span>
                    <Button
                      variant="secondary"
                      onClick={() => setPagination(prev => ({ ...prev, page: (prev.page || 1) + 1 }))}
                      disabled={!pagination.hasNext}
                      className="px-2 py-1 text-xs"
                    >
                      Next
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* User Modal */}
      {showUserModal && (
        <UserModal
          user={selectedUser}
          onClose={() => {
            setShowUserModal(false);
            setSelectedUser(null);
          }}
          onSave={() => {
            setShowUserModal(false);
            setSelectedUser(null);
            fetchUsers();
            fetchTotalCounts();
          }}
          roles={availableRoles}
          rolesLoading={rolesLoading}
          companies={companies}
        />
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && resetPasswordUser && (
        <ResetPasswordModal
          user={resetPasswordUser}
          onClose={() => {
            setShowResetPasswordModal(false);
            setResetPasswordUser(null);
          }}
          onReset={handleResetPassword}
          loading={actionLoading}
        />
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Import Users from CSV
              </h2>
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setCsvFile(null);
                  setImportPreview([]);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XCircleIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Select CSV File
                </label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-brand-primary file:text-white hover:file:bg-brand-primary/90"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Expected format: DESIGNATION, SEVARTH ID, FIRST NAME, MIDDLE NAME, LAST NAME, EMAIL ID, MOBILE NO
                </p>
              </div>

              {/* Preview */}
              {importPreview.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Preview ({importPreview.length} users)
                  </h3>
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto max-h-60">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <th className="px-3 py-2 text-left">Name</th>
                            <th className="px-3 py-2 text-left">Email</th>
                            <th className="px-3 py-2 text-left">Phone</th>
                            <th className="px-3 py-2 text-left">Badge ID</th>
                            <th className="px-3 py-2 text-left">Designation</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {importPreview.slice(0, 10).map((row, index) => (
                            <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                              <td className="px-3 py-2">
                                {`${row.first_name || ''} ${row.middle_name || ''} ${row.last_name || ''}`.trim()}
                              </td>
                              <td className="px-3 py-2">{row.email_id || row.email || 'NA'}</td>
                              <td className="px-3 py-2">{row.phone || 'NA'}</td>
                              <td className="px-3 py-2">{row.badge_no || 'NA'}</td>
                              <td className="px-3 py-2">{row.designation_id || 'NA'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {importPreview.length > 10 && (
                        <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700">
                          ... and {importPreview.length - 10} more users
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowImportModal(false);
                    setCsvFile(null);
                    setImportPreview([]);
                  }}
                  className="px-4 py-2 text-xs"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleImportUsers}
                  disabled={!csvFile || importLoading}
                  className="px-4 py-2 text-xs flex items-center gap-2"
                >
                  {importLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                      Importing...
                    </>
                  ) : (
                    <>
                      <UserIcon className="h-3 w-3" />
                      Import Users
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Results Modal */}
      {showImportResultModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] mx-4">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Import Results
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    CSV user import completed
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowImportResultModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm font-medium text-green-800 dark:text-green-200">Successful</span>
                  </div>
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
                    {importResults.successCount}
                  </p>
                </div>
                <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-800">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span className="text-sm font-medium text-red-800 dark:text-red-200">Failed</span>
                  </div>
                  <p className="text-2xl font-bold text-red-600 dark:text-red-400 mt-1">
                    {importResults.errorCount}
                  </p>
                </div>
              </div>

              {/* Error Details */}
              {importResults.errorDetails.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    Failed Users Details ({importResults.errorDetails.length})
                  </h3>
                  
                  {/* Scrollable Error List */}
                  <div className="max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                    <div className="divide-y divide-gray-200 dark:divide-gray-700">
                      {importResults.errorDetails.map((error, index) => (
                        <div key={index} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                              <span className="text-sm font-medium text-red-600 dark:text-red-400">
                                {index + 1}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                <div>
                                  <span className="font-medium text-gray-900 dark:text-white">👤 User:</span>
                                  <span className="ml-2 text-gray-600 dark:text-gray-300">{error.name}</span>
                                </div>
                                <div>
                                  <span className="font-medium text-gray-900 dark:text-white">📧 Email:</span>
                                  <span className="ml-2 text-gray-600 dark:text-gray-300">{error.email}</span>
                                </div>
                                <div>
                                  <span className="font-medium text-gray-900 dark:text-white">🆔 User ID:</span>
                                  <span className="ml-2 text-gray-600 dark:text-gray-300">{error.user_id}</span>
                                </div>
                                <div>
                                  <span className="font-medium text-gray-900 dark:text-white">🏢 Company ID:</span>
                                  <span className="ml-2 text-gray-600 dark:text-gray-300">{error.company_id}</span>
                                </div>
                                <div>
                                  <span className="font-medium text-gray-900 dark:text-white">🏷️ Badge No:</span>
                                  <span className="ml-2 text-gray-600 dark:text-gray-300">{error.badge_no}</span>
                                </div>
                                <div>
                                  <span className="font-medium text-gray-900 dark:text-white">📍 Field:</span>
                                  <span className="ml-2 text-gray-600 dark:text-gray-300">{error.field}</span>
                                </div>
                              </div>
                              <div className="mt-2">
                                <span className="font-medium text-red-600 dark:text-red-400">❌ Error:</span>
                                <span className="ml-2 text-red-700 dark:text-red-300">{error.error_message}</span>
                              </div>
                              {error.details && Object.keys(error.details).length > 0 && (
                                <div className="mt-2">
                                  <span className="font-medium text-gray-900 dark:text-white">🔍 Details:</span>
                                  <pre className="ml-2 text-xs text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 p-2 rounded mt-1 overflow-x-auto">
                                    {JSON.stringify(error.details, null, 2)}
                                  </pre>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowImportResultModal(false)}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
export default UsersPage;


